// Ported from sn-plugin2 packages/embed-contract/src/{codec,embedUrl}.ts @ 54685d64 — keep in step with the plugin.
import {
  PLUGIN_LOCAL_HOSTNAMES,
  PLUGIN_MAX_CFG_LENGTH,
  PLUGIN_PREVIEW_LOCAL_ORIGIN,
  PLUGIN_PREVIEW_PROD_ORIGIN,
  PLUGIN_VIEW_ID_FIELD,
} from '../constants/embedOptions'
import type { PluginPublicCfg, PluginView } from '../types/embedSettings'

export interface PluginPreviewInput {
  origin: string
  pluginKey: string | null
  cfg: PluginPublicCfg
  apiBaseUrl: string | null
}

export type PluginPreviewResult =
  | { url: string }
  | { reason: 'noKey' | 'noId' | 'badOrigin' | 'tooLarge' }

const ROUTE_SEGMENT: Record<PluginView, string> = {
  organization: 'org',
  competition: 'competition',
  team: 'team',
}

/** UTF-8 bytes as a binary string, without depending on TextEncoder. */
const toBinaryString = (text: string): string =>
  encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_match, hex: string) =>
    String.fromCharCode(parseInt(hex, 16))
  )

/** base64url(JSON) with UTF-8 support, identical to the plugin's `encodeCfg`. */
export const encodePluginCfg = (cfg: PluginPublicCfg): string =>
  btoa(toBinaryString(JSON.stringify(cfg)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/[=]+$/, '')

const parseHttpOrigin = (origin: string): URL | null => {
  try {
    const parsed = new URL(origin)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed
      : null
  } catch {
    return null
  }
}

/** The preview's starting plugin URL: the local plugin when this app runs locally, the deployed one otherwise. */
export const pluginPreviewDefaultOrigin = (appHostname: string): string =>
  PLUGIN_LOCAL_HOSTNAMES.includes(appHostname)
    ? PLUGIN_PREVIEW_LOCAL_ORIGIN
    : PLUGIN_PREVIEW_PROD_ORIGIN

export const buildPluginPreviewUrl = ({
  origin,
  pluginKey,
  cfg,
  apiBaseUrl,
}: PluginPreviewInput): PluginPreviewResult => {
  const base = parseHttpOrigin(origin)
  if (!base) {
    return { reason: 'badOrigin' }
  }
  if (!pluginKey) {
    return { reason: 'noKey' }
  }
  const id = cfg[PLUGIN_VIEW_ID_FIELD[cfg.view]]
  if (!id) {
    return { reason: 'noId' }
  }
  const encoded = encodePluginCfg(cfg)
  if (encoded.length > PLUGIN_MAX_CFG_LENGTH) {
    return { reason: 'tooLarge' }
  }

  const url = new URL(
    `/${ROUTE_SEGMENT[cfg.view]}/${encodeURIComponent(id)}`,
    base
  )
  url.searchParams.set('key', pluginKey)
  url.searchParams.set('cfg', encoded)
  // A local plugin build reads this app's API; deployed builds ignore `sn-*`.
  if (PLUGIN_LOCAL_HOSTNAMES.includes(base.hostname) && apiBaseUrl) {
    url.searchParams.set('sn-api', apiBaseUrl)
  }
  return { url: url.toString() }
}
